import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Avatar from '../components/ui/Avatar';
import { formatPrice, formatNumber, formatTimeAgo, formatDate } from '../utils/formatters';
import { mockAchievements } from '../data/mockAchievements';

const ProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { getUserByUsername, domains, transactions, users } = useApp();
  const [activeTab, setActiveTab] = useState('portfolio');

  const user = getUserByUsername(username);
  const userDomains = domains.filter(d => d.listedBy === username);
  const userTransactions = transactions.filter(
    t => t.seller === username || t.buyer === username
  );

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-20 text-center">
        <div className="text-6xl mb-4">👤</div>
        <h1 className="text-2xl font-bold mb-4">User Not Found</h1>
        <Button onClick={() => navigate('/')}>Go Home</Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 h-48" />

      <div className="container mx-auto px-4">
        <div className="relative -mt-20 mb-8">
          <Card className="p-6">
            <div className="flex flex-col md:flex-row items-start gap-6">
              {/* Avatar */}
              <Avatar
                src={user.avatar}
                size="2xl"
                className="-mt-16 border-4 border-white shadow-lg"
              />

              {/* User Info */}
              <div className="flex-1">
                <div className="flex flex-col md:flex-row items-start justify-between gap-4">
                  <div>
                    <h1 className="text-3xl font-bold">{user.displayName}</h1>
                    <p className="text-gray-600">@{user.username}</p>
                    {user.location && (
                      <p className="text-sm text-gray-500 mt-1">
                        📍 {user.location}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button>Follow</Button>
                    <Button variant="outline">Message</Button>
                    <Button variant="outline" size="md">
                      🔗
                    </Button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mt-3">
                  {user.badges?.map(badge => (
                    <Badge key={badge} variant="success">
                      {badge === 'topTrader' && '🏆 Top Trader'}
                      {badge === 'speedDemon' && '⚡ Speed Demon'}
                      {badge === 'eliteMember' && '💎 Elite Member'}
                    </Badge>
                  ))}
                </div>

                <p className="mt-4 text-gray-600">{user.bio}</p>

                <div className="flex gap-6 mt-4 text-sm">
                  <div>
                    <span className="font-bold">{formatNumber(user.followers)}</span>
                    <span className="text-gray-600"> followers</span>
                  </div>
                  <div>
                    <span className="font-bold">{formatNumber(user.following)}</span>
                    <span className="text-gray-600"> following</span>
                  </div>
                  <div className="text-gray-600">
                    Member since {formatDate(user.memberSince, 'short')}
                  </div>
                </div>
              </div>
            </div>
          </Card>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-8">
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">{user.stats.totalDomains}</p>
            <p className="text-sm text-gray-600">Total Domains</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">{user.stats.totalSales}</p>
            <p className="text-sm text-gray-600">Total Sales</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">{formatPrice(user.stats.totalVolume)}</p>
            <p className="text-sm text-gray-600">Volume</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">{user.stats.winRate}%</p>
            <p className="text-sm text-gray-600">Win Rate</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold">{user.stats.avgFlipTime}d</p>
            <p className="text-sm text-gray-600">Avg Flip Time</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">+{user.stats.roi}%</p>
            <p className="text-sm text-gray-600">ROI</p>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Tabs */}
            <Card className="mb-6">
              <div className="flex border-b overflow-x-auto">
                {[
                  { id: 'portfolio', label: 'Portfolio' },
                  { id: 'activity', label: 'Activity' },
                  { id: 'achievements', label: 'Achievements' },
                  { id: 'stats', label: 'Stats' },
                  { id: 'reviews', label: 'Reviews' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-6 py-4 font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </Card>

            {/* Tab Content */}
            {activeTab === 'portfolio' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex gap-2">
                    <Button variant="primary" size="sm">All</Button>
                    <Button variant="ghost" size="sm">Listed</Button>
                    <Button variant="ghost" size="sm">Unlisted</Button>
                  </div>
                  <select className="px-4 py-2 border rounded-lg">
                    <option>Recently Added</option>
                    <option>Price: Low to High</option>
                    <option>Price: High to Low</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {userDomains.map(domain => (
                    <Card
                      key={domain.id}
                      hover
                      className="p-6 cursor-pointer"
                      onClick={() => navigate(`/domain/${domain.name}`)}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-xl font-bold">{domain.name}</h3>
                        {domain.trending && <Badge variant="warning" size="sm">🔥</Badge>}
                      </div>
                      <p className="text-3xl font-bold text-blue-600 mb-4">
                        {formatPrice(domain.price)}
                      </p>
                      <div className="flex items-center justify-between text-sm text-gray-600">
                        <span>👁 {formatNumber(domain.views)}</span>
                        <span>❤️ {domain.likes}</span>
                      </div>
                    </Card>
                  ))}
                </div>

                {userDomains.length === 0 && (
                  <Card className="p-12 text-center">
                    <div className="text-6xl mb-4">📦</div>
                    <p className="text-gray-600">No domains in portfolio yet</p>
                  </Card>
                )}
              </div>
            )}

            {activeTab === 'activity' && (
              <div className="space-y-4">
                {userTransactions.slice(0, 10).map(transaction => (
                  <Card key={transaction.id} className="p-4">
                    <div className="flex items-center gap-4">
                      <div
                        className={`p-3 rounded-full ${
                          transaction.seller === username
                            ? 'bg-green-100'
                            : 'bg-blue-100'
                        }`}
                      >
                        {transaction.seller === username ? (
                          <span className="text-green-600">💰</span>
                        ) : (
                          <span className="text-blue-600">🛒</span>
                        )}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">
                          {transaction.seller === username ? 'Sold' : 'Purchased'}{' '}
                          {transaction.domain}
                        </p>
                        <p className="text-sm text-gray-600">
                          {transaction.seller === username
                            ? `to @${transaction.buyer}`
                            : `from @${transaction.seller}`}{' '}
                          • {formatTimeAgo(transaction.timestamp)}
                        </p>
                      </div>
                      <p className="text-xl font-bold">{formatPrice(transaction.price)}</p>
                    </div>
                  </Card>
                ))}
              </div>
            )}

            {activeTab === 'achievements' && (
              <div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {mockAchievements.map(achievement => {
                    const earned = user.badges?.includes(achievement.id);
                    return (
                      <Card
                        key={achievement.id}
                        className={`p-6 text-center transition-all ${
                          earned ? 'bg-gradient-to-br from-blue-50 to-purple-50' : 'opacity-50 grayscale'
                        }`}
                      >
                        <div className="text-5xl mb-3">{achievement.icon}</div>
                        <p className="font-bold mb-1">{achievement.name}</p>
                        <p className="text-sm text-gray-600 mb-2">
                          {achievement.description}
                        </p>
                        <Badge
                          variant={
                            achievement.rarity === 'legendary'
                              ? 'warning'
                              : achievement.rarity === 'epic'
                              ? 'primary'
                              : achievement.rarity === 'rare'
                              ? 'info'
                              : 'default'
                          }
                          size="sm"
                        >
                          {achievement.rarity}
                        </Badge>
                        {earned && (
                          <p className="text-xs text-gray-500 mt-2">
                            ✓ Unlocked
                          </p>
                        )}
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}

            {activeTab === 'stats' && (
              <div className="space-y-6">
                {/* Performance chart placeholder */}
                <Card className="p-6">
                  <h3 className="font-bold mb-4">Trading Performance</h3>
                  <div className="h-64 flex items-center justify-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg">
                    <p className="text-gray-500">📊 Portfolio Value Over Time</p>
                  </div>
                </Card>

                {/* Best stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="p-6">
                    <h3 className="font-bold mb-4">🏆 Most Profitable Sale</h3>
                    <p className="text-2xl font-bold mb-2">AIRevolution.com</p>
                    <div className="space-y-1 text-sm">
                      <p>Bought for: {formatPrice(1200)}</p>
                      <p>Sold for: {formatPrice(12000)}</p>
                      <p className="text-green-600 font-bold">ROI: 900%</p>
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-bold mb-4">⚡ Fastest Flip</h3>
                    <p className="text-2xl font-bold mb-2">QuickStart.com</p>
                    <div className="space-y-1 text-sm">
                      <p>Time to sell: 4 hours</p>
                      <p>Profit: {formatPrice(3500)}</p>
                    </div>
                  </Card>
                </div>

                {/* Category breakdown */}
                <Card className="p-6">
                  <h3 className="font-bold mb-4">📊 Category Breakdown</h3>
                  <div className="space-y-3">
                    {['Tech', 'Crypto', 'Finance', 'Health'].map((category, index) => {
                      const percentage = [40, 30, 20, 10][index];
                      return (
                        <div key={category}>
                          <div className="flex justify-between mb-1 text-sm">
                            <span>{category}</span>
                            <span>{percentage}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 mb-6">
                  <div className="text-5xl font-bold">{user.rating}</div>
                  <div>
                    <div className="flex gap-1 mb-1">
                      {[...Array(5)].map((_, i) => (
                        <span
                          key={i}
                          className={
                            i < Math.floor(user.rating)
                              ? 'text-yellow-400 text-xl'
                              : 'text-gray-300 text-xl'
                          }
                        >
                          ★
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">
                      Based on {user.reviewCount} reviews
                    </p>
                  </div>
                </div>

                {/* Review list */}
                {[...Array(5)].map((_, i) => (
                  <Card key={i} className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar src={`https://i.pravatar.cc/150?img=${i + 10}`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="font-bold">@user{i + 1}</p>
                          <div className="flex gap-0.5">
                            {[...Array(5)].map((_, j) => (
                              <span key={j} className="text-yellow-400 text-sm">
                                ★
                              </span>
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600 mb-2">
                          Great seller! Fast response, smooth transaction. Highly
                          recommended.
                        </p>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          <span>Domain: Example{i + 1}.com</span>
                          <span>•</span>
                          <span>
                            {formatTimeAgo(
                              new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="p-6">
              <h3 className="font-bold mb-4">Quick Actions</h3>
              <div className="space-y-2">
                <Button fullWidth variant="outline">
                  Send Message
                </Button>
                <Button fullWidth variant="outline">
                  Propose Trade
                </Button>
                <Button fullWidth variant="outline">
                  View Active Listings
                </Button>
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-bold mb-4">Similar Traders</h3>
              <div className="space-y-3">
                {users.slice(0, 3).map(similarUser => (
                  <div
                    key={similarUser.username}
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                    onClick={() => navigate(`/profile/${similarUser.username}`)}
                  >
                    <Avatar src={similarUser.avatar} size="sm" />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {similarUser.displayName}
                      </p>
                      <p className="text-xs text-gray-600">
                        {similarUser.stats.totalSales} sales
                      </p>
                    </div>
                    <Button size="sm" variant="outline">
                      Follow
                    </Button>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-6">
              <h3 className="font-bold mb-4">🏆 Rankings</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Volume</span>
                  <span className="font-bold">#12</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Sales</span>
                  <span className="font-bold">#8</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ROI</span>
                  <span className="font-bold">#5</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Speed</span>
                  <span className="font-bold">#3</span>
                </div>
              </div>
              <Button
                fullWidth
                className="mt-4"
                size="sm"
                onClick={() => navigate('/leaderboard')}
              >
                View Leaderboard
              </Button>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
